var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { AccordionSet, Button, Checkbox, ErrorModal, Icon, Loading, MessageBanner, Modal, ModalFooter, Select, TextLink, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useForm, useWatch, FormProvider, } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useOkapiKy, useCallout } from "@folio/stripes/core";
import { ProductAccessibilityEnum, } from "../../types/Api";
import { createProductWithNumber, fetchProductsByTitleId, updateProductAccessibility, } from "../../api/productService";
import { fetchProductNumbersByTitleId } from "../../api/productNumberService";
import { useAssignFeasibility } from "../../hooks/useAssignFeasibility";
import { usePermissions } from "../../hooks/usePermissions";
import { useCanAssignProductNumber } from "../../hooks/useCanAssignProductNumber";
import { useOnixOptions } from "../../hooks/title/useOnixOptions";
import { defaultPublishingStatus, firstOnix } from "../../utils/selectMappers";
import { defaultPrimaryContentType } from "../../utils/productContentType";
import { isPublishingStatusBlockingAssign } from "../../utils/constants";
import { formatPriceForDisplay } from "../../utils/priceUtils";
import { getAccessibilityTranslation } from "../../utils/getAccessibilityTranslation";
import { buildProductPayload } from "../../utils/buildProductPayload";
import { createProductSchema } from "../../utils/validationUtils";
import { getLockedPrimaryContentType } from "../../utils/onixDependencies";
import { getErrorCode } from "../../utils/getErrorCode";
import { resolveProductCreateErrorMessageId } from "../../utils/productCreateErrors";
import { MAGNITUDE_NOT_ALLOWED_MESSAGE_ID, PREFIX_SIZE_NOT_AVAILABLE_MESSAGE_ID, } from "../../utils/newPrefixMagnitude";
import { NEW_PREFIX_BY_PUBLISHER_ONLY_MESSAGE_ID, NO_NUMBER_REASON_MESSAGE_IDS, } from "../../utils/productNumberAssignErrors";
import { useNewPrefixMagnitude } from "../../hooks/useNewPrefixMagnitude";
import { NewPrefixMagnitudeChoice } from "../productNumber/NewPrefixMagnitudeChoice";
import { useScrollToError } from "../../hooks/useScrollToError";
import { ProductGeneralSection } from "../product/ProductGeneralSection";
import { ProductPrimaryContentTypeMismatchModal } from "../product/ProductPrimaryContentTypeMismatchModal";
import { ProductMeasuresSection } from "../product/ProductMeasuresSection";
import { ProductExtentsSection } from "../product/ProductExtentsSection";
import { ProductDetailsSection } from "../product/ProductDetailsSection";
import { ProductPriceSection } from "../product/ProductPriceSection";
import { ProductRelationsSection } from "../product/ProductRelationsSection";
import { InfoBox, InfoBoxTypeEnum } from "../InfoBox";
import { ProductNumberAssignModal } from "./ProductNumberAssignModal";
import { ProductEditModal } from "./ProductEditModal";
import { ProductAccessibilityConfirmModal } from "../ProductAccessibilityConfirmModal";
import { ProductPublishingDateRequiredModal } from "../ProductPublishingDateRequiredModal";
import { PendingDeclarationPrompt } from "../PendingDeclarationPrompt";
import { useCreateProductConfirm } from "../../hooks/useCreateProductConfirm";
import { CreateAnotherProductConfirmModal } from "./CreateAnotherProductConfirmModal";
import styles from "./TitleProductsPanel.module.css";
/** Small inline barcode glyph rendered next to a product number. */
var BarcodeIcon = function () { return (React.createElement("svg", { className: styles.barcode, width: "20", height: "16", viewBox: "0 0 20 16", fill: "currentColor", "aria-hidden": "true" },
    React.createElement("rect", { x: "0", y: "0", width: "1.5", height: "16" }),
    React.createElement("rect", { x: "3", y: "0", width: "1", height: "16" }),
    React.createElement("rect", { x: "5.5", y: "0", width: "2", height: "16" }),
    React.createElement("rect", { x: "9", y: "0", width: "1", height: "16" }),
    React.createElement("rect", { x: "11.5", y: "0", width: "1.5", height: "16" }),
    React.createElement("rect", { x: "14.5", y: "0", width: "1", height: "16" }),
    React.createElement("rect", { x: "17", y: "0", width: "2", height: "16" }))); };
/**
 * A single product card. Shows the product form, price and its ISBN/ISMN (when
 * assigned); for products without a number it offers an assign action that
 * opens the shared {@link ProductNumberAssignModal}.
 */
var ProductCard = function (_a) {
    var _b, _c;
    var product = _a.product, productNumber = _a.productNumber, onAssign = _a.onAssign, onEdit = _a.onEdit, onPublish = _a.onPublish, highlighted = _a.highlighted;
    var intl = useIntl();
    var _d = usePermissions(), canEditProductItem = _d.canEditProductItem, canChangeProductAccessibility = _d.canChangeProductAccessibility;
    var canAssignProductNumber = useCanAssignProductNumber(product).canAssignProductNumber;
    var productId = product.id;
    var formName = (typeof product.form === "string"
        ? product.form
        : (_b = product.form) === null || _b === void 0 ? void 0 : _b.description) || "-";
    var priceText = product.price && product.price.amount != null
        ? "".concat(formatPriceForDisplay(product.price.amount), " ").concat(((_c = product.price.currency) === null || _c === void 0 ? void 0 : _c.value) || "").trim()
        : null;
    var isPublic = product.accessibility === ProductAccessibilityEnum.PUBLIC;
    var accessibilityText = getAccessibilityTranslation(intl, product.accessibility);
    var fullNumber = (productNumber === null || productNumber === void 0 ? void 0 : productNumber.fullFormattedValue) ||
        (productNumber === null || productNumber === void 0 ? void 0 : productNumber.formattedValue) ||
        (productNumber === null || productNumber === void 0 ? void 0 : productNumber.value);
    return (React.createElement("div", { className: "".concat(styles.card, " ").concat(highlighted ? styles.cardHighlight : "") },
        React.createElement("div", { className: styles.cardHead },
            React.createElement("span", { className: styles.formBadge }, formName),
            priceText && React.createElement("span", { className: styles.price }, priceText)),
        fullNumber ? (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.numberRow },
                React.createElement(BarcodeIcon, null),
                React.createElement("span", { className: styles.numberValue }, fullNumber)),
            (productNumber === null || productNumber === void 0 ? void 0 : productNumber.note) && (React.createElement("div", { className: styles.numberNote }, productNumber.note)))) : (React.createElement("div", { className: styles.warnStrip },
            React.createElement("span", null,
                React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.noNumber" })),
            canAssignProductNumber && (React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: function () { return productId && onAssign(productId); } },
                React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.assignNumber" }))))),
        React.createElement("div", { className: styles.metaRow },
            product.publishingDate && (React.createElement("span", null,
                React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.publishing" }),
                " ",
                product.publishingDate)),
            React.createElement("span", { className: "".concat(styles.pill, " ").concat(isPublic ? styles.pillPublic : styles.pillPrivate) }, accessibilityText)),
        React.createElement("div", { className: styles.links },
            React.createElement(TextLink, { to: "/bragency/products/detail/".concat(productId) },
                React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.detail" })),
            canEditProductItem(product) && (React.createElement("button", { type: "button", className: styles.linkButton, onClick: function () { return productId && onEdit(productId); } },
                React.createElement(FormattedMessage, { id: "ui-br-agency.button.edit" }))),
            !isPublic && canChangeProductAccessibility(product) && (React.createElement("button", { type: "button", className: styles.linkButton, onClick: function () { return onPublish(product); } },
                React.createElement(FormattedMessage, { id: "ui-br-agency.product.button.makePublic" }))))));
};
/** Maps a feasibility reason code to its localized message id. */
/**
 * Whether the product was created without the requested number, so the dialog stays open and explains why: the user
 * still has to pick a prefix size ({@code needsPublisherPrefix}), or the backend names a reason that stops it.
 */
export var isNumberNotAssigned = function (assignRequested, response) {
    return assignRequested && (response.needsPublisherPrefix || !!response.reason);
};
export var FEASIBILITY_REASON_MESSAGE = __assign({ PUBLISHER_CLOSED: "ui-br-agency.title.products.panel.assign.reason.closed", DATA_NOT_CONFIRMED: "ui-br-agency.title.products.panel.assign.reason.dataNotConfirmed", NO_AGENCY_PREFIX: "ui-br-agency.title.products.panel.assign.reason.noAgencyPrefix", NO_AVAILABLE_NUMBERS: "ui-br-agency.title.products.panel.assign.noNumbers", DECLARATION_PENDING: "ui-br-agency.title.products.panel.assign.reason.declarationPending", NEW_PREFIX_BY_PUBLISHER_ONLY: NEW_PREFIX_BY_PUBLISHER_ONLY_MESSAGE_ID }, NO_NUMBER_REASON_MESSAGE_IDS);
/**
 * Maps a create-with-number failure reason to its localized message id. Only reasons that need a different
 * wording than the generic "no prefix with free numbers" notice are listed.
 */
export var CREATE_FAILURE_REASON_MESSAGE = __assign({ INVALID_PUBLISHER_PREFIX_SELECTION: "ui-br-agency.title.products.panel.create.prefixNotUsable", DECLARATION_PENDING: "ui-br-agency.title.products.panel.assign.reason.declarationPending", PREFIX_MAGNITUDE_NOT_ALLOWED: MAGNITUDE_NOT_ALLOWED_MESSAGE_ID, NEW_PREFIX_BY_PUBLISHER_ONLY: NEW_PREFIX_BY_PUBLISHER_ONLY_MESSAGE_ID, NO_AVAILABLE_NUMBERS: "ui-br-agency.title.products.panel.assign.noNumbers", PREFIX_SIZE_NOT_AVAILABLE: PREFIX_SIZE_NOT_AVAILABLE_MESSAGE_ID }, NO_NUMBER_REASON_MESSAGE_IDS);
/** Base path of the publisher-prefixes list within this app. */
var PUBLISHER_PREFIXES_PATH = "/bragency/publisher-prefixes";
/**
 * Builds the publisher-prefixes list route, pre-filtered to a single publisher when its source id is
 * known. The list (see usePublisherPrefixFilters) reads the `publisherId` URL param and filters by
 * `publisher.sourceId`; when the source id is absent the plain, unfiltered list is opened.
 */
export var buildPublisherPrefixesPath = function (publisherSourceId) {
    return publisherSourceId
        ? "".concat(PUBLISHER_PREFIXES_PATH, "?publisherId=").concat(encodeURIComponent(publisherSourceId))
        : PUBLISHER_PREFIXES_PATH;
};
/**
 * Actionable guidance rendered only for the DECLARATION_PENDING reason. It names the path to the
 * (otherwise buried) declaration screen — Agentura → Prefixy nakladatele → vyberte prefix → Akce →
 * "Zadat poslední použité číslo" — and offers a button that closes the create dialog and navigates to
 * the publisher-prefixes list, pre-filtered to the title's publisher when its source id is known.
 */
export var DeclarationPendingGuidance = function (_a) {
    var publisherSourceId = _a.publisherSourceId, onNavigate = _a.onNavigate;
    var history = useHistory();
    var handleClick = function () {
        // Close the modal first so the user lands on the list cleanly, then navigate.
        onNavigate();
        history.push(buildPublisherPrefixesPath(publisherSourceId));
    };
    return (React.createElement("div", { className: styles.declarationGuidance },
        React.createElement("p", { className: styles.declarationGuidanceHint },
            React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.assign.reason.declarationPending.hint" })),
        React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: handleClick },
            React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.assign.reason.declarationPending.action" }))));
};
/**
 * Modal with the complete product creation form (all product sections) plus a
 * checkbox to assign an ISBN/ISMN number right away. Creating the product and
 * assigning its number happen in a single backend call.
 */
var ProductCreateModal = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var title = _a.title, onClose = _a.onClose, onCreated = _a.onCreated, onError = _a.onError, requiredPrimaryContentType = _a.requiredPrimaryContentType;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var callout = useCallout();
    var queryClient = useQueryClient();
    var _k = useOnixOptions(), primaryContentTypeTable = _k.primaryContentTypeTable, compositionTable = _k.compositionTable, publishingStatusTable = _k.publishingStatusTable;
    var _l = useState(false), showPrice = _l[0], setShowPrice = _l[1];
    var _m = useState(true), assignNumber = _m[0], setAssignNumber = _m[1];
    // Shown when the user tries to save a product with a primary content type that
    // differs from the title's established type.
    var _o = useState(false), contentTypeMismatchOpen = _o[0], setContentTypeMismatchOpen = _o[1];
    // Set once the product is created but a number could not be assigned — shows
    // an inline notice right here in the create dialog.
    var _p = useState(false), createdNeedsPrefix = _p[0], setCreatedNeedsPrefix = _p[1];
    // Reason code the backend reported for that failure, so the notice can name it.
    var _q = useState(), createdFailureReason = _q[0], setCreatedFailureReason = _q[1];
    var _r = useState({
        general: true,
        measures: true,
        extents: true,
        details: true,
        price: true,
        relations: true,
    }), accordionStatus = _r[0], setAccordionStatus = _r[1];
    var toggleAccordion = function (section) {
        return setAccordionStatus(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[section] = !prev[section], _a)));
        });
    };
    // Recomputed once the ONIX codetables finish loading (then applied via reset
    // below). publishingStatus shares defaultPublishingStatus with the full-page
    // ProductCreate so both create paths default to "Neurčeno" (00), never
    // "Zrušeno" (01), and primaryContentType shares defaultPrimaryContentType
    // (locked type → Text → empty, never the first codetable item).
    var initialValues = useMemo(function () { return ({
        title: title,
        ownerId: undefined,
        primaryContentType: defaultPrimaryContentType(primaryContentTypeTable, requiredPrimaryContentType),
        // Left empty on purpose so the user consciously picks the binding (form)
        // instead of silently accepting an arbitrary first codetable value.
        form: undefined,
        formDetails: [],
        unitMeasures: undefined,
        cartonMeasures: undefined,
        palletMeasures: undefined,
        extents: [],
        composition: firstOnix(compositionTable),
        publishingStatus: defaultPublishingStatus(publishingStatusTable),
        publishingDate: "",
        price: undefined,
        accessibility: ProductAccessibilityEnum.PRIVATE,
        publicTime: undefined,
    }); }, [
        title,
        primaryContentTypeTable,
        compositionTable,
        publishingStatusTable,
        requiredPrimaryContentType,
    ]);
    var methods = useForm({
        resolver: zodResolver(createProductSchema(formatMessage)),
        defaultValues: initialValues,
        mode: "onSubmit",
    });
    var handleSubmit = methods.handleSubmit, reset = methods.reset, errors = methods.formState.errors;
    useScrollToError(errors);
    useEffect(function () {
        reset(initialValues);
    }, [initialValues, reset]);
    var primaryContentTypeValue = (_b = useWatch({
        control: methods.control,
        name: "primaryContentType",
    })) === null || _b === void 0 ? void 0 : _b.value;
    var publishingStatusValue = (_c = useWatch({
        control: methods.control,
        name: "publishingStatus",
    })) === null || _c === void 0 ? void 0 : _c.value;
    // A Cancelled (01) / Postponed indefinitely (03) product must never get a number, so assignment is
    // switched off client-side (the feasibility endpoint cannot know the status before the product exists).
    var assignForbiddenByStatus = isPublishingStatusBlockingAssign(publishingStatusValue);
    var assignRequested = assignNumber && !assignForbiddenByStatus;
    var _s = useAssignFeasibility({
        titleId: title.id,
        assignNumber: assignRequested,
        primaryContentTypeValue: primaryContentTypeValue,
    }), feasibility = _s.feasibility, assignBlocked = _s.assignBlocked, showBlockSize = _s.showBlockSize, prefixOptions = _s.prefixOptions, effectivePrefixId = _s.effectivePrefixId, submitBlocked = _s.submitBlocked, setSelectedPrefixId = _s.setSelectedPrefixId, agencyMessageId = _s.agencyMessageId;
    var showPrefixPicker = assignRequested && prefixOptions.length > 1;
    // Block size of a new prefix, capped by the publisher's production (FSD PP20) — smallest allowed preselected.
    var _t = useNewPrefixMagnitude(feasibility === null || feasibility === void 0 ? void 0 : feasibility.maxMagnitude, feasibility === null || feasibility === void 0 ? void 0 : feasibility.allowedMagnitudes), magnitude = _t.magnitude, setMagnitude = _t.setMagnitude;
    // Only actually attempt assignment when it's requested, allowed for the status, AND feasible.
    var effectiveAssign = assignRequested && !assignBlocked;
    // After an inline declaration, re-check feasibility so the DECLARATION_PENDING block clears and the
    // user can finish with "Vytvořit produkt". Only the feasibility answer is invalidated — the form
    // values the user already entered stay untouched, and no product is created automatically.
    var handleDeclared = function () {
        queryClient.invalidateQueries(["assignFeasibility"]);
    };
    var createMutation = useMutation(function (payload) {
        return createProductWithNumber(ky, {
            product: payload,
            assignProductNumber: effectiveAssign,
            magnitude: effectiveAssign && showBlockSize ? magnitude : undefined,
            publisherPrefixId: effectiveAssign && effectivePrefixId ? effectivePrefixId : undefined,
        });
    }, {
        onSuccess: function (response) {
            var _a, _b, _c, _d;
            // Product is always created — refresh the list and highlight the new one.
            onCreated(response.product.id);
            if (isNumberNotAssigned(effectiveAssign, response)) {
                // Number could not be assigned (no prefix with free numbers, the chosen
                // prefix was rejected, or every prefix still awaits its declaration).
                // Keep the dialog open and say why right here.
                setCreatedFailureReason((_a = response.reason) !== null && _a !== void 0 ? _a : undefined);
                setCreatedNeedsPrefix(true);
                return;
            }
            var assignedValue = ((_b = response.productNumber) === null || _b === void 0 ? void 0 : _b.fullFormattedValue) ||
                ((_c = response.productNumber) === null || _c === void 0 ? void 0 : _c.formattedValue) ||
                ((_d = response.productNumber) === null || _d === void 0 ? void 0 : _d.value);
            callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                type: "success",
                message: assignedValue
                    ? formatMessage({
                        id: "ui-br-agency.title.products.panel.create.success.number",
                    }, { value: assignedValue })
                    : formatMessage({
                        id: "ui-br-agency.title.products.panel.create.success",
                    }),
            });
            onClose();
        },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var code;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorCode(error)];
                    case 1:
                        code = _a.sent();
                        onError(formatMessage({ id: resolveProductCreateErrorMessageId(code) }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var onSubmit = function (data) {
        var _a;
        // All products of one title must share the same primary content type. The
        // field is pre-filled but editable; if the user picked a different type, show
        // the info dialog and stop instead of creating the product.
        if ((requiredPrimaryContentType === null || requiredPrimaryContentType === void 0 ? void 0 : requiredPrimaryContentType.value) &&
            ((_a = data.primaryContentType) === null || _a === void 0 ? void 0 : _a.value) &&
            data.primaryContentType.value !== requiredPrimaryContentType.value) {
            setContentTypeMismatchOpen(true);
            return;
        }
        createMutation.mutate(buildProductPayload(data, {
            title: title,
            showPrice: showPrice,
        }));
    };
    var footer = createdNeedsPrefix ? (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: onClose },
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.close" })))) : (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", disabled: createMutation.isLoading || submitBlocked, onClick: handleSubmit(onSubmit) }, createMutation.isLoading ? (React.createElement(React.Fragment, null,
            React.createElement(Icon, { icon: "spinner-ellipsis", size: "small" }),
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.saving" }))) : (React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.quickCreate.submit" }))),
        React.createElement(Button, { onClick: onClose, disabled: createMutation.isLoading },
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.cancel" }))));
    return (React.createElement(React.Fragment, null,
        React.createElement(Modal, { open: true, dismissible: !createMutation.isLoading, onClose: onClose, size: "large", label: formatMessage({
                id: "ui-br-agency.title.products.panel.quickCreate.title",
            }), footer: footer },
            createMutation.isLoading && (React.createElement("div", { className: styles.savingInfo },
                React.createElement(Icon, { icon: "spinner-ellipsis" }),
                React.createElement("span", null,
                    React.createElement(FormattedMessage, { id: assignNumber
                            ? "ui-br-agency.title.products.panel.create.saving.withNumber"
                            : "ui-br-agency.title.products.panel.create.saving" })))),
            createdNeedsPrefix ? (React.createElement(MessageBanner, { type: "warning" },
                React.createElement(FormattedMessage, { id: (_d = (createdFailureReason &&
                        CREATE_FAILURE_REASON_MESSAGE[createdFailureReason])) !== null && _d !== void 0 ? _d : "ui-br-agency.title.products.panel.create.numberNotAssigned" }),
                createdFailureReason === "DECLARATION_PENDING" && (React.createElement(PendingDeclarationPrompt, { publisherSourceId: (_e = title.owner) === null || _e === void 0 ? void 0 : _e.sourceId, agency: feasibility === null || feasibility === void 0 ? void 0 : feasibility.agency, onDeclared: handleDeclared, fallback: React.createElement(DeclarationPendingGuidance, { publisherSourceId: (_f = title.owner) === null || _f === void 0 ? void 0 : _f.sourceId, onNavigate: onClose }) })))) : (React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles.assignRow },
                    React.createElement(Checkbox, { label: formatMessage({
                            id: "ui-br-agency.title.products.panel.quickCreate.assign",
                        }), checked: assignNumber, onChange: function (e) {
                            return setAssignNumber(e.target.checked);
                        } }),
                    assignNumber && assignForbiddenByStatus && (React.createElement(MessageBanner, { type: "warning" },
                        React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.create.statusNotAssignable" }))),
                    assignRequested && (feasibility === null || feasibility === void 0 ? void 0 : feasibility.agency) && (React.createElement("div", { className: styles.agencyHint },
                        React.createElement(FormattedMessage, { id: agencyMessageId }))),
                    assignBlocked && (feasibility === null || feasibility === void 0 ? void 0 : feasibility.reason) && (React.createElement(MessageBanner, { type: "error" },
                        React.createElement(FormattedMessage, { id: (_g = FEASIBILITY_REASON_MESSAGE[feasibility.reason]) !== null && _g !== void 0 ? _g : "ui-br-agency.title.products.panel.create.numberNotAssigned" }),
                        feasibility.reason === "DECLARATION_PENDING" && (React.createElement(PendingDeclarationPrompt, { publisherSourceId: (_h = title.owner) === null || _h === void 0 ? void 0 : _h.sourceId, agency: feasibility.agency, onDeclared: handleDeclared, fallback: React.createElement(DeclarationPendingGuidance, { publisherSourceId: (_j = title.owner) === null || _j === void 0 ? void 0 : _j.sourceId, onNavigate: onClose }) })))),
                    showPrefixPicker && (React.createElement("div", { className: styles.prefixPicker },
                        React.createElement(Select, { label: formatMessage({
                                id: "ui-br-agency.title.products.panel.assign.choosePrefix",
                            }), value: effectivePrefixId, onChange: function (e) {
                                return setSelectedPrefixId(e.target.value);
                            }, dataOptions: prefixOptions.map(function (p) { return ({
                                value: p.publisherPrefixId,
                                label: p.label,
                            }); }), marginBottom0: true }))),
                    showBlockSize && (React.createElement(NewPrefixMagnitudeChoice, { maxMagnitude: feasibility === null || feasibility === void 0 ? void 0 : feasibility.maxMagnitude, allowedMagnitudes: feasibility === null || feasibility === void 0 ? void 0 : feasibility.allowedMagnitudes, replacesUsedUpPrefix: feasibility === null || feasibility === void 0 ? void 0 : feasibility.replacesUsedUpPrefix, magnitude: magnitude, onChange: setMagnitude, name: "createMagnitude", fieldsetClassName: styles.magnitudeFieldset, hintClassName: styles.magnitudeHint }))),
                React.createElement(FormProvider, __assign({}, methods),
                    React.createElement("form", { noValidate: true, onSubmit: handleSubmit(onSubmit) },
                        React.createElement(AccordionSet, null,
                            React.createElement(ProductGeneralSection, { open: accordionStatus.general, onToggle: function () { return toggleAccordion("general"); } }),
                            React.createElement(ProductMeasuresSection, { open: accordionStatus.measures, onToggle: function () { return toggleAccordion("measures"); } }),
                            React.createElement(ProductExtentsSection, { open: accordionStatus.extents, onToggle: function () { return toggleAccordion("extents"); } }),
                            React.createElement(ProductDetailsSection, { open: accordionStatus.details, onToggle: function () { return toggleAccordion("details"); } }),
                            React.createElement(ProductPriceSection, { showPrice: showPrice, onShowPriceChange: setShowPrice, open: accordionStatus.price, onToggle: function () { return toggleAccordion("price"); } }),
                            React.createElement(ProductRelationsSection, { open: accordionStatus.relations, onToggle: function () { return toggleAccordion("relations"); } }))))))),
        React.createElement(ProductPrimaryContentTypeMismatchModal, { open: contentTypeMismatchOpen, onClose: function () { return setContentTypeMismatchOpen(false); } })));
};
/** Local error-modal state (kept inline to avoid an extra shared hook). */
var useLocalError = function () {
    var _a = useState(false), isOpen = _a[0], setIsOpen = _a[1];
    var _b = useState(""), message = _b[0], setMessage = _b[1];
    var openModal = function (msg) {
        setMessage(msg);
        setIsOpen(true);
    };
    var closeModal = function () { return setIsOpen(false); };
    return { isOpen: isOpen, message: message, openModal: openModal, closeModal: closeModal };
};
/**
 * "Produkty" panel showing a title's product formats and their ISBN/ISMN
 * numbers, with an inline assign flow and a create-product modal. The panel
 * title (with the product count) is rendered separately by `ProductsPaneTitle`.
 */
var TitleProductsPanel = function (_a) {
    var _b;
    var title = _a.title, autoOpenCreate = _a.autoOpenCreate;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var callout = useCallout();
    var queryClient = useQueryClient();
    var canCreateProductForTitle = usePermissions().canCreateProductForTitle;
    var titleId = title === null || title === void 0 ? void 0 : title.id;
    // A publisher-only user gets no "Vytvořit produkt" on a ONE_OFF owner's title
    // (not even via ?newProduct=1 auto-open).
    var canCreate = canCreateProductForTitle(title);
    var _c = useState(false), isCreateModalOpen = _c[0], setIsCreateModalOpen = _c[1];
    var _d = useState(null), assignProductId = _d[0], setAssignProductId = _d[1];
    var _e = useState(null), editProductId = _e[0], setEditProductId = _e[1];
    // Product pending "make public" confirmation, and one blocked on a missing
    // publishing date.
    var _f = useState(null), publishTarget = _f[0], setPublishTarget = _f[1];
    var _g = useState(null), dateRequiredProduct = _g[0], setDateRequiredProduct = _g[1];
    // "Vytvořit produkt" asks first when the title already has a product.
    var openCreateModal = useCallback(function () { return setIsCreateModalOpen(true); }, []);
    var _h = useCreateProductConfirm(openCreateModal), requestCreateProduct = _h.requestCreate, createProductConfirmModalProps = _h.modalProps;
    // Open the create modal once when navigated here via "Přidat produkt". The
    // user already confirmed at the origin, so this never asks again.
    var autoOpenedRef = useRef(false);
    useEffect(function () {
        if (autoOpenCreate && canCreate && titleId && !autoOpenedRef.current) {
            autoOpenedRef.current = true;
            setIsCreateModalOpen(true);
        }
    }, [autoOpenCreate, canCreate, titleId]);
    // Transiently highlights the just-added/assigned product (cleared on reload).
    var _j = useState(null), highlightProductId = _j[0], setHighlightProductId = _j[1];
    var _k = useLocalError(), errorOpen = _k.isOpen, errorMessage = _k.message, openError = _k.openModal, closeError = _k.closeModal;
    var _l = useQuery(["products", titleId], function () { return fetchProductsByTitleId(ky, titleId); }, { enabled: !!titleId }), productsData = _l.data, productsLoading = _l.isLoading;
    var _m = useQuery(["productNumbers", "byTitle", titleId], function () { return fetchProductNumbersByTitleId(ky, titleId); }, { enabled: !!titleId }), productNumbersData = _m.data, numbersLoading = _m.isLoading;
    // Merge products with their assigned numbers (mirrors TitleDetail merging).
    var productsWithNumbers = useMemo(function () {
        var _a;
        var products = (productsData === null || productsData === void 0 ? void 0 : productsData.items) || [];
        var productNumbers = (productNumbersData === null || productNumbersData === void 0 ? void 0 : productNumbersData.items) || [];
        var numberByProductId = new Map();
        for (var _i = 0, productNumbers_1 = productNumbers; _i < productNumbers_1.length; _i++) {
            var pn = productNumbers_1[_i];
            var pid = (_a = pn.product) === null || _a === void 0 ? void 0 : _a.id;
            if (pid && !numberByProductId.has(pid)) {
                numberByProductId.set(pid, pn);
            }
        }
        return products.map(function (product) { return ({
            product: product,
            productNumber: product.id ? numberByProductId.get(product.id) : undefined,
        }); });
    }, [productsData, productNumbersData]);
    var refresh = function () {
        queryClient.invalidateQueries(["products", titleId]);
        queryClient.invalidateQueries(["productNumbers", "byTitle", titleId]);
        queryClient.invalidateQueries(["productNumbers"]);
    };
    var handleCreated = function (newProductId) {
        refresh();
        if (newProductId)
            setHighlightProductId(newProductId);
    };
    var publishMutation = useMutation(function (product) { return updateProductAccessibility(ky, product.id, "PUBLIC"); }, {
        onSuccess: function (_data, product) {
            setPublishTarget(null);
            refresh();
            if (product.id)
                setHighlightProductId(product.id);
            callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                type: "success",
                message: formatMessage({
                    id: "ui-br-agency.title.products.panel.publish.success",
                }),
            });
        },
        onError: function () {
            setPublishTarget(null);
            openError(formatMessage({ id: "ui-br-agency.product.error.update" }));
        },
    });
    // Guard: making a product public requires a publishing date.
    var handlePublish = function (product) {
        if (!product.publishingDate || product.publishingDate.trim() === "") {
            setDateRequiredProduct(product);
            return;
        }
        setPublishTarget(product);
    };
    var isLoading = productsLoading || numbersLoading;
    var count = productsWithNumbers.length;
    return (React.createElement("div", { className: styles.panel },
        !!titleId && canCreate && (React.createElement("div", { className: styles.createRow },
            React.createElement(Button, { buttonStyle: "primary", fullWidth: true, marginBottom0: true, onClick: function () {
                    return requestCreateProduct(titleId, 
                    // Unknown while loading — the hook then looks the count up.
                    productsData ? count > 0 : undefined);
                } },
                React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.quickCreate.submit" })))),
        !titleId ? (React.createElement(InfoBox, { type: InfoBoxTypeEnum.INFO, titleId: "ui-br-agency.title.products.panel.notSaved.title" },
            React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.notSaved.text" }))) : isLoading ? (React.createElement("div", { className: styles.loading },
            React.createElement(Loading, null))) : count === 0 ? (React.createElement(InfoBox, { type: InfoBoxTypeEnum.WARNING, titleId: "ui-br-agency.title.products.panel.empty.title" },
            React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.empty.text" }))) : (React.createElement("div", { className: styles.cards }, productsWithNumbers.map(function (_a) {
            var product = _a.product, productNumber = _a.productNumber;
            return (React.createElement(ProductCard, { key: product.id, product: product, productNumber: productNumber, onAssign: setAssignProductId, onEdit: setEditProductId, onPublish: handlePublish, highlighted: !!product.id && product.id === highlightProductId }));
        }))),
        !!title && canCreate && isCreateModalOpen && (React.createElement(ProductCreateModal, { title: title, onClose: function () { return setIsCreateModalOpen(false); }, onCreated: handleCreated, onError: openError, requiredPrimaryContentType: getLockedPrimaryContentType(productsData === null || productsData === void 0 ? void 0 : productsData.items) })),
        React.createElement(CreateAnotherProductConfirmModal, __assign({}, createProductConfirmModalProps)),
        React.createElement(ProductAccessibilityConfirmModal, { open: !!publishTarget, productName: publishTarget
                ? typeof publishTarget.form === "string"
                    ? publishTarget.form
                    : ((_b = publishTarget.form) === null || _b === void 0 ? void 0 : _b.description) || ""
                : "", pendingAccessibility: ProductAccessibilityEnum.PUBLIC, onConfirm: function () { return publishTarget && publishMutation.mutate(publishTarget); }, onCancel: function () { return setPublishTarget(null); } }),
        React.createElement(ProductPublishingDateRequiredModal, { open: !!dateRequiredProduct, onEdit: function () {
                var _a;
                var id = (_a = dateRequiredProduct === null || dateRequiredProduct === void 0 ? void 0 : dateRequiredProduct.id) !== null && _a !== void 0 ? _a : null;
                setDateRequiredProduct(null);
                if (id)
                    setEditProductId(id);
            }, onCancel: function () { return setDateRequiredProduct(null); } }),
        editProductId && (React.createElement(ProductEditModal, { productId: editProductId, onClose: function () { return setEditProductId(null); }, onSaved: function (savedId) {
                setEditProductId(null);
                refresh();
                setHighlightProductId(savedId);
            }, onError: openError })),
        assignProductId && (React.createElement(ProductNumberAssignModal, { productId: assignProductId, onClose: function () { return setAssignProductId(null); }, onAssigned: function (numberLabel) {
                var assigned = assignProductId;
                setAssignProductId(null);
                refresh();
                setHighlightProductId(assigned);
                callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                    type: "success",
                    message: numberLabel
                        ? formatMessage({
                            id: "ui-br-agency.product-number.assign.success.assigned",
                        }, { value: numberLabel })
                        : formatMessage({
                            id: "ui-br-agency.title.products.panel.assign.assigned",
                        }),
                });
            }, onError: openError })),
        React.createElement(ErrorModal, { open: errorOpen, label: formatMessage({ id: "ui-br-agency.modal.label.error" }), content: errorMessage, onClose: closeError })));
};
/**
 * Pane header title "Produkty" with the current product count badge. Shares the
 * products query (same key) with the panel, so it adds no extra request.
 */
export var ProductsPaneTitle = function (_a) {
    var _b;
    var titleId = _a.titleId;
    var ky = useOkapiKy();
    var data = useQuery(["products", titleId], function () { return fetchProductsByTitleId(ky, titleId); }, { enabled: !!titleId }).data;
    var count = (_b = data === null || data === void 0 ? void 0 : data.items) === null || _b === void 0 ? void 0 : _b.length;
    return (React.createElement("span", { className: styles.paneTitle },
        React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.heading" }),
        count != null && React.createElement("span", { className: styles.countPill }, count)));
};
export default TitleProductsPanel;
